import html2canvas from "html2canvas";
import PptxGenJS from "pptxgenjs";
import { Document, HeadingLevel, Packer, Paragraph, TextRun } from "docx";
import jsPDF from "jspdf";
import JSZip from "jszip";

const DEFAULT_FILENAME = "worship-export";
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const normaliseHex = (hex) => {
  if (!hex || typeof hex !== "string") return null;
  const trimmed = hex.trim();
  if (!/^#?[0-9a-f]{3,6}$/i.test(trimmed)) return null;
  const normalized = trimmed.startsWith("#") ? trimmed.slice(1) : trimmed;
  if (normalized.length === 3) {
    return `#${normalized[0]}${normalized[0]}${normalized[1]}${normalized[1]}${normalized[2]}${normalized[2]}`;
  }
  if (normalized.length === 6) {
    return `#${normalized}`;
  }
  return null;
};

const hexToRgb = (hex) => {
  const normalized = normaliseHex(hex);
  if (!normalized) return null;
  const value = Number.parseInt(normalized.slice(1), 16);
  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
};

const mixColors = (foreground, background, amount = 0.5) => {
  const fg = hexToRgb(foreground);
  const bg = hexToRgb(background);
  if (!fg || !bg) return foreground || background || "#000000";
  const mix = (c1, c2) => Math.round(c1 * amount + c2 * (1 - amount));
  return `#${mix(fg.r, bg.r).toString(16).padStart(2, "0")}${mix(fg.g, bg.g)
    .toString(16)
    .padStart(2, "0")}${mix(fg.b, bg.b).toString(16).padStart(2, "0")}`;
};

const lightenColor = (color, amount = 0.2) => {
  const rgb = hexToRgb(color);
  if (!rgb) return color;
  const mix = (channel) => clamp(channel + 255 * amount, 0, 255);
  return `#${mix(rgb.r).toString(16).padStart(2, "0")}${mix(rgb.g)
    .toString(16)
    .padStart(2, "0")}${mix(rgb.b).toString(16).padStart(2, "0")}`;
};

const sanitizeHtmlToText = (value) => {
  if (!value) return "";
  const parser = new DOMParser();
  const doc = parser.parseFromString(value, "text/html");
  return doc.body.textContent || "";
};

export const normaliseParagraph = (paragraph) => {
  if (!paragraph) return "";
  if (paragraph.toLowerCase().includes("<img")) {
    const parser = new DOMParser();
    const frag = parser.parseFromString(paragraph, "text/html");
    const img = frag.querySelector("img");
    if (img?.src) {
      return `[Image: ${img.src}]`;
    }
    return "[Image]";
  }
  return sanitizeHtmlToText(paragraph).replace(/\r\n/g, "\n");
};

const ensureArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return [value];
};

const clampValue = (value, min, max) => Math.min(Math.max(value, min), max);

const detectLinesPerSlide = (song) => {
  const lyrics = ensureArray(song?.lyrics);
  if (!lyrics.length) return 0;
  const fullText = lyrics.filter((p) => typeof p === "string").join(" ").toLowerCase();
  const hasLatin = /[a-z]/i.test(fullText);
  const hasTamil = /[\u0B80-\u0BFF]/.test(fullText);
  const hasMalayalam = /[\u0D00-\u0D7F]/.test(fullText);
  const scriptCount = [hasLatin, hasTamil, hasMalayalam].filter(Boolean).length;
  if (scriptCount < 2) return 0;
  const firstTextParagraph = lyrics.find(
    (p) => typeof p === "string" && !p.toLowerCase().includes("<img")
  );
  if (!firstTextParagraph) return 0;
  const count = firstTextParagraph
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean).length;
  return clampValue(count || 0, 1, 25);
};

const formatLyricsForExport = (song) => {
  const normalizedLyrics = ensureArray(song?.lyrics).map((p) => normaliseParagraph(p));
  const explicit = Number.isFinite(song?.linesPerSlide) ? song.linesPerSlide : 0;
  const auto = explicit > 0 ? explicit : detectLinesPerSlide(song);
  const linesPerSlide = clampValue(auto || 0, 0, 25);
  if (!linesPerSlide) {
    return { lyrics: normalizedLyrics, linesPerSlide: 0 };
  }
  const chunks = [];
  let buffer = [];
  const flushBuffer = () => {
    if (buffer.length) {
      chunks.push(buffer.join("\n"));
      buffer = [];
    }
  };
  normalizedLyrics.forEach((paragraph) => {
    if (paragraph.toLowerCase().includes("<img")) {
      flushBuffer();
      chunks.push(paragraph);
      return;
    }
    const lines = paragraph
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    lines.forEach((line) => {
      buffer.push(line);
      if (buffer.length >= linesPerSlide) {
        flushBuffer();
      }
    });
  });
  flushBuffer();
  return { lyrics: chunks.length ? chunks : normalizedLyrics, linesPerSlide };
};

const prepareSongForExport = (song) => {
  const { lyrics, linesPerSlide } = formatLyricsForExport(song);
  return { ...song, lyrics, linesPerSlide };
};

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const captureElement = async (elementOrSelector) => {
  const element =
    typeof elementOrSelector === "string"
      ? document.querySelector(elementOrSelector)
      : elementOrSelector;
  if (!element) {
    throw new Error("Unable to find element for export.");
  }
  return html2canvas(element, { scale: 2, useCORS: true, backgroundColor: null });
};

export const exportCurrentViewAsImage = async (element, filename = DEFAULT_FILENAME) => {
  const canvas = await captureElement(element);
  const dataUrl = canvas.toDataURL("image/png");
  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = `${filename}.png`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const exportCurrentViewAsPpt = async (element, filename = DEFAULT_FILENAME) => {
  const canvas = await captureElement(element);
  const image = canvas.toDataURL("image/png");
  const pptx = new PptxGenJS();
  const slide = pptx.addSlide();
  const { width, height } = pptx.presLayout;
  slide.background = { color: "FFFFFF" };
  slide.addImage({
    data: image,
    x: 0,
    y: 0,
    w: width,
    h: height,
  });
  await pptx.writeFile({ fileName: `${filename}.pptx` });
};

const fontSources = [
  {
    id: "NotoSans-Regular",
    filename: "NotoSans-Regular.ttf",
    urls: [
      `${process.env.PUBLIC_URL || ""}/fonts/NotoSans-Regular.ttf`,
      "https://raw.githubusercontent.com/googlefonts/noto-fonts/main/hinted/ttf/NotoSans/NotoSans-Regular.ttf",
    ],
  },
  {
    id: "NotoSansTamil-Regular",
    filename: "NotoSansTamil-Regular.ttf",
    urls: [
      `${process.env.PUBLIC_URL || ""}/fonts/NotoSansTamil-Regular.ttf`,
      "https://raw.githubusercontent.com/googlefonts/noto-fonts/main/hinted/ttf/NotoSansTamil/NotoSansTamil-Regular.ttf",
    ],
    test: (text) => /[\u0B80-\u0BFF]/.test(text),
  },
  {
    id: "NotoSansMalayalam-Regular",
    filename: "NotoSansMalayalam-Regular.ttf",
    urls: [
      `${process.env.PUBLIC_URL || ""}/fonts/NotoSansMalayalam-Regular.ttf`,
      "https://raw.githubusercontent.com/googlefonts/noto-fonts/main/hinted/ttf/NotoSansMalayalam/NotoSansMalayalam-Regular.ttf",
    ],
    test: (text) => /[\u0D00-\u0D7F]/.test(text),
  },
];

const base64FromBuffer = (buffer) => {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const fontCache = new Map();
const availablePdfFonts = new Set();

const fetchFontData = async (font) => {
  if (fontCache.has(font.id)) {
    return fontCache.get(font.id);
  }
  for (const url of font.urls) {
    if (!url) continue;
    try {
      const res = await fetch(url);
      if (!res.ok) continue;
      const buffer = await res.arrayBuffer();
      const base64 = base64FromBuffer(buffer);
      fontCache.set(font.id, base64);
      return base64;
    } catch (error) {
      console.warn(`Failed to fetch font ${font.id} from ${url}`, error);
    }
  }
  throw new Error(`Unable to load font data for ${font.id}`);
};

const ensurePdfFonts = async (doc) => {
  for (const font of fontSources) {
    try {
      const data = await fetchFontData(font);
      doc.addFileToVFS(font.filename, data);
      doc.addFont(font.filename, font.id, "normal", "Identity-H");
      availablePdfFonts.add(font.id);
    } catch (error) {
      console.warn(`Skipping font ${font.id} for PDF export`, error);
    }
  }
  // Ensure a built-in font is selected even if none of the custom fonts loaded.
  try {
    doc.setFont("helvetica", "normal");
  } catch (e) {
    // ignore
  }
};

const chooseFont = (text) => {
  const baseFont = availablePdfFonts.has("NotoSans-Regular")
    ? "NotoSans-Regular"
    : "helvetica";
  if (!text) return baseFont;
  if (/[\u0B80-\u0BFF]/.test(text) && availablePdfFonts.has("NotoSansTamil-Regular")) {
    return "NotoSansTamil-Regular";
  }
  if (/[\u0D00-\u0D7F]/.test(text) && availablePdfFonts.has("NotoSansMalayalam-Regular")) {
    return "NotoSansMalayalam-Regular";
  }
  return baseFont;
};

const setFontSafe = (doc, fontName, style = "normal") => {
  const attempts = [
    [fontName, style],
    [fontName, "normal"],
    ["helvetica", style],
    ["helvetica", "normal"],
    ["times", style],
    ["times", "normal"],
    ["courier", style],
    ["courier", "normal"],
  ];
  for (const [name, s] of attempts) {
    if (!name) continue;
    try {
      doc.setFont(name, s);
      return name;
    } catch (err) {
      // try next
    }
  }
  console.warn(`Failed to set any font for ${fontName || "undefined"}.`);
  return null;
};

const resolvePreferredPdfFont = (preferredId, text) => {
  const preferredMap = {
    "noto-sans": "NotoSans-Regular",
    "noto-sans-tamil": "NotoSansTamil-Regular",
    "noto-sans-malayalam": "NotoSansMalayalam-Regular",
  };
  const preferred = preferredMap[preferredId];
  if (preferred && availablePdfFonts.has(preferred)) {
    return preferred;
  }
  return chooseFont(text);
};

const applyFontWithFallback = (doc, preferredId, text, style = "normal") => {
  const preferred = resolvePreferredPdfFont(preferredId, text);
  const used = setFontSafe(doc, preferred, style);
  if (used) return used;
  const safeStyle = style === "bold" ? "bold" : "normal";
  const fallback = setFontSafe(doc, "helvetica", safeStyle);
  if (fallback) return fallback;
  return setFontSafe(doc, null, safeStyle) || "helvetica";
};

const ensureActiveFont = (doc, style = "normal") => {
  const current = doc?.internal?.getFont?.();
  if (current && current.metadata && current.metadata.Unicode) {
    return current;
  }
  setFontSafe(doc, "helvetica", style);
  return doc.internal.getFont();
};

const buildDocxParagraphsForSong = (song) => {
  const paragraphs = [];
  paragraphs.push(
    new Paragraph({
      text: song.title || "Untitled Song",
      heading: HeadingLevel.HEADING_1,
      spacing: { after: 200 },
      style: "Heading1",
    })
  );
  const categories = ensureArray(song.categories).filter(Boolean);
  if (categories.length) {
    paragraphs.push(
      new Paragraph({
        children: [
          new TextRun({ text: "Categories: ", bold: true, font: "Noto Sans" }),
          new TextRun({ text: categories.join(", "), font: "Noto Sans" }),
        ],
        spacing: { after: 200 },
      })
    );
  }
  const lyrics = ensureArray(formatLyricsForExport(song).lyrics);
  lyrics.forEach((paragraph, index) => {
    const normalised = normaliseParagraph(paragraph);
    const lines = normalised.split("\n");
    const runs = lines.flatMap((line, idx) => {
      const parts = [];
      parts.push(new TextRun({ text: line, font: "Noto Sans" }));
      if (idx < lines.length - 1) {
        parts.push(new TextRun({ break: 1, font: "Noto Sans" }));
      }
      return parts;
    });
    paragraphs.push(
      new Paragraph({
        children: runs.length ? runs : [new TextRun("")],
        spacing: { after: 120 },
      })
    );
  });
  return paragraphs;
};

export const exportSongsToDocx = async (songs, filename = `${DEFAULT_FILENAME}.docx`) => {
  if (!Array.isArray(songs) || songs.length === 0) {
    throw new Error("No songs available for DOCX export.");
  }
  const doc = new Document({
    sections: [
      {
        properties: {},
        children: songs.flatMap((song, index) => {
          const paragraphs = buildDocxParagraphsForSong(prepareSongForExport(song));
          if (index < songs.length - 1) {
            paragraphs.push(
              new Paragraph({
                children: [new TextRun({ break: 1 })],
                pageBreakBefore: true,
              })
            );
          }
          return paragraphs;
        }),
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  triggerDownload(blob, filename);
};

const addSongToPdf = (doc, song, context) => {
  const { margin, lineHeight, pageHeight, pageWidth } = context;
  let y = context.cursorY;
  const availableWidth = pageWidth - margin * 2;

  const ensureSpace = (amount) => {
    if (y + amount > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  };

  doc.setFont(chooseFont(song.title), "normal");
  doc.setFontSize(16);
  const titleLines = doc.splitTextToSize(song.title || "Untitled Song", availableWidth);
  titleLines.forEach((line) => {
    ensureSpace(lineHeight);
    doc.text(line, margin, y);
    y += lineHeight;
  });

  const categories = ensureArray(song.categories).filter(Boolean);
  if (categories.length) {
    const categoryText = `Categories: ${categories.join(", ")}`;
    doc.setFont(chooseFont(categoryText), "normal");
    doc.setFontSize(11);
    const catLines = doc.splitTextToSize(categoryText, availableWidth);
    catLines.forEach((line) => {
      doc.setFont(chooseFont(line), "normal");
      ensureSpace(lineHeight);
      doc.text(line, margin, y);
      y += lineHeight;
    });
    y += 4;
  }

  doc.setFontSize(12);
  const lyrics = ensureArray(formatLyricsForExport(song).lyrics);
  lyrics.forEach((paragraph) => {
    const normalised = normaliseParagraph(paragraph);
    const paraLines = normalised.split("\n");
    paraLines.forEach((paraLine) => {
      const wrapped = doc.splitTextToSize(paraLine || " ", availableWidth);
      wrapped.forEach((line) => {
        doc.setFont(chooseFont(line), "normal");
        ensureSpace(lineHeight);
        doc.text(line, margin, y);
        y += lineHeight;
      });
    });
    y += lineHeight / 2;
  });

  context.cursorY = y + lineHeight;
  if (context.cursorY > pageHeight - margin) {
    doc.addPage();
    context.cursorY = margin;
  }
};

export const exportSongsToPdf = async (songs, filename = `${DEFAULT_FILENAME}.pdf`) => {
  if (!Array.isArray(songs) || songs.length === 0) {
    throw new Error("No songs available for PDF export.");
  }
  const attemptTextExport = async () => {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "pt",
      format: "letter",
    });

    await ensurePdfFonts(doc);

    const hasUnicodeFonts =
      availablePdfFonts.has("NotoSansTamil-Regular") ||
      availablePdfFonts.has("NotoSansMalayalam-Regular") ||
      availablePdfFonts.has("NotoSans-Regular");

    const requiresUnicodeSupport = songs.some(songContainsComplexScript);

    if (requiresUnicodeSupport && !hasUnicodeFonts) {
      throw new Error("Unicode fonts unavailable");
    }

    const context = {
      margin: 48,
      lineHeight: 18,
      pageHeight: doc.internal.pageSize.getHeight(),
      pageWidth: doc.internal.pageSize.getWidth(),
      cursorY: 48,
    };

    songs.map(prepareSongForExport).forEach((song, index) => {
      if (index > 0) {
        doc.addPage();
        context.cursorY = context.margin;
      }
      addSongToPdf(doc, song, context);
    });

    doc.save(filename);
  };

  try {
    await attemptTextExport();
  } catch (error) {
    console.warn("Text-based PDF export failed, falling back to images.", error);
    await exportSongsToPdfViaImages(songs, filename);
  }
};

const buildSongPlaintext = (song) => {
  const prepared = prepareSongForExport(song);
  const categories = ensureArray(prepared.categories).filter(Boolean);
  const parts = [prepared.title || "Untitled Song"];
  if (categories.length) {
    parts.push(`Categories: ${categories.join(", ")}`);
  }
  const lyrics = ensureArray(prepared.lyrics);
  lyrics.forEach((paragraph) => {
    const cleaned = normaliseParagraph(paragraph);
    parts.push(cleaned);
  });
  return parts.join("\n\n");
};

export const exportSongsCollectionToPpt = async (songs, filename = `${DEFAULT_FILENAME}.pptx`, styleOverrides) => {
  if (!Array.isArray(songs) || songs.length === 0) {
    throw new Error("No songs available for PowerPoint export.");
  }
  const pptx = new PptxGenJS();
  pptx.layout = "LAYOUT_WIDE";
  for (const original of songs) {
    const song = prepareSongForExport(original);
    const dataUrl = await renderSongToCanvas(song, styleOverrides);
    const slide = pptx.addSlide();
    slide.background = { color: "1F1F1F" };
    const { width, height } = pptx.presLayout;
    const margin = 0.25;
    slide.addImage({
      data: dataUrl,
      x: margin,
      y: margin,
      w: width - margin * 2,
      h: height - margin * 2,
    });
  }
  await pptx.writeFile({ fileName: filename });
};

const renderSongToCanvas = async (song, styleOverrides = {}) => {
  const exportSong = prepareSongForExport(song);
  const defaultStyles = {
    width: 1280,
    height: 720,
    background: "#000000",
    text: "#ffffff",
    headerBg: "rgba(0, 0, 0, 0.65)",
    headerBorder: "1px solid rgba(255, 255, 255, 0.15)",
    bodyPadding: "32px 80px",
  };
  const styles = { ...defaultStyles, ...styleOverrides };

  const container = document.createElement("div");
  const width = styles.width;
  const height = styles.height;
  container.style.width = `${width}px`;
  container.style.height = `${height}px`;
  container.style.background = styles.background;
  container.style.color = styles.text;
  container.style.fontFamily = "'Noto Sans', 'Noto Sans Tamil', 'Noto Sans Malayalam', sans-serif";
  container.style.display = "flex";
  container.style.flexDirection = "column";
  container.style.boxSizing = "border-box";
  container.style.justifyContent = "flex-start";
  container.style.alignItems = "stretch";

  const titleBar = document.createElement("div");
  titleBar.style.padding = "18px 24px";
  titleBar.style.fontSize = "30px";
  titleBar.style.fontWeight = "700";
  titleBar.style.textAlign = "center";
  titleBar.style.color = styles.text;
  titleBar.style.opacity = "0.9";
  titleBar.style.lineHeight = "1.35";
  titleBar.style.whiteSpace = "normal";
  titleBar.style.wordBreak = "break-word";
  titleBar.style.maxWidth = "92%";
  titleBar.style.margin = "0 auto";
  titleBar.textContent = exportSong.title || "Untitled Song";
  container.appendChild(titleBar);

  const header = document.createElement("div");
  header.textContent = exportSong.title || "Untitled Song";
  header.style.padding = "16px 20px";
  header.style.fontSize = "22px";
  header.style.fontWeight = "700";
  header.style.textAlign = "center";
  header.style.textTransform = "uppercase";
  header.style.letterSpacing = "0.04em";
  header.style.background = styles.headerBg;
  header.style.boxShadow = "0 6px 18px rgba(0, 0, 0, 0.25)";
  header.style.borderBottom = styles.headerBorder;
  header.style.color = styles.text;
  header.style.whiteSpace = "normal";
  header.style.wordBreak = "break-word";
  header.style.maxWidth = "96%";
  header.style.margin = "0 auto";
  container.appendChild(header);

  const body = document.createElement("div");
  body.style.flex = "1";
  body.style.display = "flex";
  body.style.flexDirection = "column";
  body.style.justifyContent = "center";
  body.style.alignItems = "center";
  body.style.padding = styles.bodyPadding;
  body.style.gap = "52px";
  body.style.textAlign = "center";
  body.style.color = styles.text;
  container.appendChild(body);

  const lyricsWrapper = document.createElement("div");
  lyricsWrapper.style.display = "flex";
  lyricsWrapper.style.flexDirection = "column";
  lyricsWrapper.style.gap = "42px";
  lyricsWrapper.style.fontSize = "26px";
  lyricsWrapper.style.lineHeight = "2.1";
  lyricsWrapper.style.maxHeight = "100%";
  lyricsWrapper.style.width = "68%";
  lyricsWrapper.style.margin = "0 auto";
  lyricsWrapper.style.wordBreak = "break-word";
  lyricsWrapper.style.whiteSpace = "pre-line";
  lyricsWrapper.style.color = styles.text;
  const paragraphs = ensureArray(exportSong.lyrics);
  if (!paragraphs.length) {
    const empty = document.createElement("div");
    empty.textContent = "No lyrics available.";
    lyricsWrapper.appendChild(empty);
  } else {
    paragraphs.forEach((paragraph) => {
      const block = document.createElement("div");
      block.style.padding = "0 16px";
      block.style.color = styles.text;
      block.style.lineHeight = "2.2";
      block.style.marginBottom = "18px";
      block.innerHTML = normaliseParagraph(paragraph)
        .split("\n")
        .map((line) => (line ? line : "&nbsp;"))
        .join("<br/>");
      lyricsWrapper.appendChild(block);
    });
  }
  body.appendChild(lyricsWrapper);

  const categories = ensureArray(exportSong.categories).filter(Boolean);
  if (categories.length) {
    const footer = document.createElement("div");
    footer.textContent = `Categories: ${categories.join(", ")}`;
    footer.style.fontSize = "20px";
    footer.style.opacity = "0.75";
    footer.style.marginTop = "auto";
    footer.style.textAlign = "center";
    footer.style.color = styles.text;
    body.appendChild(footer);
  }

  document.body.appendChild(container);

  const canvas = await html2canvas(container, {
    scale: 2,
    useCORS: true,
    backgroundColor: styles.background,
  });

  document.body.removeChild(container);
  return canvas.toDataURL("image/png");
};

const exportSongsToPdfViaImages = async (songs, filename, styleOverrides) => {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "letter",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  for (let i = 0; i < songs.length; i += 1) {
    const prepared = prepareSongForExport(songs[i]);
    if (i > 0) doc.addPage();
    const dataUrl = await renderSongToCanvas(prepared, styleOverrides);
    const img = new Image();
    img.src = dataUrl;
    await new Promise((resolve) => {
      img.onload = resolve;
    });
    const imgWidth = img.width;
    const imgHeight = img.height;
    const margin = 24;
    const ratio = Math.min((pageWidth - margin * 2) / imgWidth, (pageHeight - margin * 2) / imgHeight);
    const displayWidth = imgWidth * ratio;
    const displayHeight = imgHeight * ratio;
    const x = (pageWidth - displayWidth) / 2;
    const y = margin; // top-align to reduce excess space between songs
    doc.addImage(dataUrl, "PNG", x, y, displayWidth, displayHeight, undefined, "FAST");
  }

  doc.save(filename);
};

const songContainsComplexScript = (song) => {
  const text = [song.title, ...ensureArray(song.lyrics)].join("\n");
  return /[^\u0000-\u00ff]/.test(text);
};

const buildPalette = (theme = {}, forcePrintFriendly = false) => {
  if (forcePrintFriendly) {
    return {
      background: "#ffffff",
      text: "#111111",
      accent: "#4a4a4a",
      muted: "#666666",
    };
  }
  const background = normaliseHex(theme.background) || "#ffffff";
  const text = normaliseHex(theme.text) || "#111111";
  const accent = normaliseHex(theme.gradient) || normaliseHex(theme.accent);
  const derivedAccent = accent || lightenColor(background, 0.18) || "#3f51b5";
  return {
    background,
    text,
    accent: derivedAccent,
    muted: mixColors(text, background, 0.65),
  };
};

const prepareBookContext = (options = {}) => {
  const palette = buildPalette(options.theme, options.forcePrintFriendly);
  const ensureReadablePalette = (pal) => {
    // If text and background are too close, force a high-contrast fallback.
    const isValidHex = (v) => /^#[0-9a-f]{6}$/i.test(v || "");
    const bg = isValidHex(pal.background) ? pal.background : "#ffffff";
    const fg = isValidHex(pal.text) ? pal.text : "#111111";
    const contrast = (color) => {
      const c = parseInt(color.slice(1), 16);
      const r = (c >> 16) & 255;
      const g = (c >> 8) & 255;
      const b = c & 255;
      const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return lum;
    };
    const diff = Math.abs(contrast(bg) - contrast(fg));
    if (diff < 0.45) {
      return {
        background: "#ffffff",
        text: "#111111",
        accent: pal.accent || "#444444",
        muted: "#666666",
      };
    }
    return pal;
  };
  const safePalette = ensureReadablePalette(palette);
  const bodyFontSize = clamp(options.bodyFontSize || 13, 11, 18);
  const lineHeight = clamp(options.lineHeight || Math.round(bodyFontSize * 1.35), 14, 28);
  const columns = clamp(options.columns || 2, 1, 3);
  return {
    palette: safePalette,
    bodyFontSize,
    lineHeight,
    headingSize: clamp(bodyFontSize + 10, 18, 30),
    subheadingSize: clamp(bodyFontSize + 2, 14, 22),
    columns,
    columnGap: 22,
    margin: 56,
    preferredFontId: options.preferredFontId,
    boldLyrics: Boolean(options.boldLyrics),
    settingsSummary: options.settingsSummary || "",
    plainLines: Boolean(options.plainLines),
  };
};

const drawCoverPage = (doc, context, metadata) => {
  const { palette, headingSize, subheadingSize, bodyFontSize, preferredFontId } = context;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const centerX = pageWidth / 2;
  doc.setFillColor(palette.background);
  doc.rect(0, 0, pageWidth, pageHeight, "F");

  const topPadding = context.margin * 1.4;
  const stack = [];

  const mainTitle = metadata.bookTitle || "Worship Songbook";
  stack.push({
    type: "text",
    text: mainTitle,
    fontSize: Math.min(Math.max(headingSize + 4, 28), 52),
    gap: context.lineHeight * 1.8,
    maxWidth: pageWidth - context.margin * 2,
    color: palette.text,
    lineGap: context.lineHeight * 1.2,
  });

  if (metadata.subtitle) {
    stack.push({
      type: "text",
      text: metadata.subtitle,
      fontSize: Math.min(Math.max(subheadingSize + 2, 18), 36),
      gap: context.lineHeight * 1.6,
      maxWidth: pageWidth - context.margin * 2,
      color: palette.text,
      lineGap: context.lineHeight * 1,
    });
  }

  const detailLines = [
    metadata.catalogLabel ? `Catalog: ${metadata.catalogLabel}` : "",
    metadata.categoryLabel ? `Category: ${metadata.categoryLabel}` : "",
    metadata.generatedAt ? `Generated: ${metadata.generatedAt}` : "",
    metadata.settingsSummary ? `Settings: ${metadata.settingsSummary}` : "",
  ].filter(Boolean);

  if (detailLines.length) {
    stack.push({
      type: "multiline",
      lines: detailLines,
      fontSize: bodyFontSize + 1,
      gap: context.lineHeight,
      maxWidth: pageWidth - context.margin * 2,
      color: palette.muted,
      lineGap: context.lineHeight * 1.05,
    });
  }

  // compute total height
  const totalHeight = stack.reduce((sum, item) => {
    if (item.type === "logo") return sum + item.height + item.gap;
    if (item.type === "text") {
      const lines = doc.splitTextToSize(item.text, item.maxWidth || pageWidth - context.margin * 2);
      return sum + lines.length * (item.fontSize * 1.1) + item.gap;
    }
    if (item.type === "multiline") {
      return sum + item.lines.length * (item.fontSize * 1.15) + item.gap;
    }
    return sum;
  }, 0);

  let cursorY = Math.max(topPadding, (pageHeight - totalHeight) / 2);

  stack.forEach((item) => {
    if (item.type === "logo") {
      doc.addImage(
        metadata.logoDataUrl,
        "PNG",
        centerX - item.width / 2,
        cursorY,
        item.width,
        item.height
      );
      cursorY += item.height + item.gap;
      return;
    }
    if (item.type === "text") {
      applyFontWithFallback(doc, preferredFontId, item.text, "normal");
      ensureActiveFont(doc, "normal");
      doc.setFontSize(item.fontSize);
      doc.setTextColor(item.color);
      const lines = doc.splitTextToSize(item.text, item.maxWidth || pageWidth - context.margin * 2);
      const step = item.lineGap || item.fontSize * 1.2;
      lines.forEach((line, idx) => {
        doc.text(line, centerX, cursorY + idx * step, { align: "center" });
      });
      cursorY += lines.length * step + item.gap;
      return;
    }
    if (item.type === "multiline") {
      applyFontWithFallback(doc, preferredFontId, item.lines.join(" "), "normal");
      ensureActiveFont(doc, "normal");
      doc.setFontSize(item.fontSize);
      doc.setTextColor(item.color);
      const step = item.lineGap || item.fontSize * 1.15;
      item.lines.forEach((line, idx) => {
        doc.text(line, centerX, cursorY + idx * step, { align: "center" });
      });
      cursorY += item.lines.length * step + item.gap;
    }
  });
};

const fillPageBackground = (doc, palette) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.setFillColor(palette.background);
  doc.rect(0, 0, pageWidth, pageHeight, "F");
};

const renderSongSection = (doc, song, context, layoutState, includeTitle = true) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const columnWidth =
    (pageWidth - context.margin * 2 - context.columnGap * (context.columns - 1)) /
    context.columns;

  const state =
    layoutState || {
      page: doc.getNumberOfPages(),
      columnIndex: 0,
      cursorX: context.margin,
      cursorY: context.margin + context.lineHeight * 0.6,
    };

  const resetX = () =>
    context.margin + state.columnIndex * (columnWidth + context.columnGap);

  let cursorX = resetX();
  let cursorY = state.cursorY + (includeTitle ? context.lineHeight * 0.4 : 0);
  let activeFont = applyFontWithFallback(doc, context.preferredFontId, song.title, "normal");
  const pagesUsed = new Set([state.page]);

  const headerTop = (includeTitleHeader = true) => {
    fillPageBackground(doc, context.palette);
    if (includeTitleHeader) {
      activeFont = applyFontWithFallback(doc, context.preferredFontId, song.title, "normal");
      ensureActiveFont(doc, "normal");
      doc.setFontSize(context.headingSize);
      doc.setTextColor(context.palette.text);
      const titleLines = doc.splitTextToSize(song.title || "Untitled Song", columnWidth);
      const titleStep = context.lineHeight * 1.15;
      titleLines.forEach((line, idx) => {
        doc.text(line, cursorX, cursorY + idx * titleStep, { maxWidth: columnWidth });
      });
      cursorY += titleLines.length * titleStep + context.lineHeight * 0.9;
      const categories = ensureArray(song.categories).filter(Boolean);
      if (categories.length) {
        doc.setFontSize(context.subheadingSize);
        doc.setTextColor(context.palette.muted);
        const catText = `Categories: ${categories.join(", ")}`;
        const catLines = doc.splitTextToSize(catText, columnWidth);
        const catStep = context.lineHeight * 1.05;
        catLines.forEach((line, idx) => {
          doc.text(line, cursorX, cursorY + idx * catStep, {
            maxWidth: columnWidth,
          });
        });
        cursorY += catLines.length * catStep + context.lineHeight * 0.9;
      }
      doc.setDrawColor(context.palette.accent);
      doc.setLineWidth(0.75);
      doc.line(cursorX, cursorY, cursorX + columnWidth, cursorY);
      cursorY += context.lineHeight * 0.8;
    } else {
      cursorY += context.lineHeight * 0.6;
    }
  };

  const moveToNextColumn = () => {
    if (state.columnIndex < context.columns - 1) {
      state.columnIndex += 1;
      cursorX = resetX();
      cursorY = context.margin + context.lineHeight * 0.6;
    } else {
      doc.addPage();
      fillPageBackground(doc, context.palette);
      pagesUsed.add(doc.getNumberOfPages());
      state.page = doc.getNumberOfPages();
      state.columnIndex = 0;
      cursorX = resetX();
      cursorY = context.margin + context.lineHeight * 0.6;
      headerTop(false);
    }
  };

  const ensureSpace = (amount) => {
    if (cursorY + amount > pageHeight - context.margin) {
      moveToNextColumn();
    }
  };

  if (includeTitle) {
    const minHeaderSpace = context.lineHeight * 2.5;
    if (cursorY + minHeaderSpace > pageHeight - context.margin) {
      moveToNextColumn();
      cursorX = resetX();
    }
    headerTop(true);
  } else {
    headerTop(false);
  }

  // Ensure a valid font is active before measuring/splitting lyrics.
  applyFontWithFallback(doc, context.preferredFontId, song.title, "normal");
  ensureActiveFont(doc, "normal");
  doc.setFontSize(context.bodyFontSize);
  const paragraphs = ensureArray(song.lyrics);
  const breakLongTokens = (text) => {
    if (!text) return "";
    return text.replace(/(\S{18})(?=\S)/g, "$1 ");
  };
  const wrapLines = (text) => doc.splitTextToSize(breakLongTokens(text) || " ", columnWidth);

  paragraphs.forEach((paragraph) => {
    const cleaned = normaliseParagraph(paragraph);
    applyFontWithFallback(doc, context.preferredFontId, cleaned, "normal");
    ensureActiveFont(doc, "normal");

    const logicalLines = context.plainLines
      ? cleaned.split(/\r?\n/).filter((l) => l.trim().length > 0)
      : [cleaned];

    logicalLines.forEach((logicalLine) => {
      const lines = wrapLines(logicalLine);
      lines.forEach((line) => {
        activeFont = applyFontWithFallback(doc, context.preferredFontId, line, "normal");
        ensureActiveFont(doc, "normal");
        doc.setFontSize(context.bodyFontSize);
        doc.setTextColor(context.palette.text);
        if (context.boldLyrics) {
          const boldApplied = setFontSafe(doc, activeFont, "bold");
          if (!boldApplied) {
            applyFontWithFallback(doc, context.preferredFontId, line, "normal");
          }
        }
        ensureSpace(context.lineHeight * 1.1);
        doc.text(line || " ", cursorX, cursorY);
        cursorY += context.lineHeight;
      });
      if (!context.plainLines) {
        cursorY += context.lineHeight * 0.3;
      }
    });
  });

  state.cursorX = cursorX;
  state.cursorY = cursorY;
  return { state, pagesUsed };
};

const renderTableOfContents = (doc, tocPages, entries, context, metadata) => {
  if (!tocPages.length) return;
  const pageWidth = doc.internal.pageSize.getWidth();
  const linesPerPage = Math.max(
    1,
    Math.floor(
      (doc.internal.pageSize.getHeight() - context.margin * 2 - context.lineHeight * 1.5) /
        context.lineHeight
    )
  );
  let entryIndex = 0;
  tocPages.forEach((pageNumber, pageIdx) => {
    doc.setPage(pageNumber);
    doc.setFillColor(context.palette.background);
    doc.rect(0, 0, pageWidth, doc.internal.pageSize.getHeight(), "F");
    // Header book title / category
    doc.setTextColor(context.palette.text);
    const headerLeft = metadata.bookTitle || "Worship Songbook";
    const headerRight = (metadata.categoryLabel || "").toUpperCase();
    const headerY = context.margin - context.lineHeight * 0.2;
    doc.setFontSize(context.subheadingSize);
    applyFontWithFallback(doc, context.preferredFontId, headerLeft, "normal");
    ensureActiveFont(doc, "normal");
    doc.text(headerLeft, context.margin, headerY, { maxWidth: pageWidth / 2 - context.margin });
    if (headerRight) {
      doc.text(headerRight, pageWidth - context.margin, headerY, {
        align: "right",
        maxWidth: pageWidth / 2 - context.margin,
      });
    }

    applyFontWithFallback(doc, context.preferredFontId, "Table of Contents", "normal");
    ensureActiveFont(doc, "normal");
    doc.setFontSize(context.headingSize - 2);
    const titleY = context.margin + context.lineHeight * 1.6;
    doc.text("Table of Contents", context.margin, titleY);
    doc.setFontSize(context.bodyFontSize);
    doc.setTextColor(context.palette.muted);
    const subtitleParts = [];
    if (metadata?.catalogLabel) subtitleParts.push(metadata.catalogLabel);
    if (metadata?.categoryLabel) subtitleParts.push(metadata.categoryLabel);
    if (subtitleParts.length) {
      doc.text(subtitleParts.join(" • "), context.margin, titleY + context.lineHeight * 1.1);
    }

    doc.setFontSize(context.bodyFontSize);
    doc.setTextColor(context.palette.text);
    let cursorY = titleY + context.lineHeight * 2.6;
    for (let line = 0; line < linesPerPage && entryIndex < entries.length; line += 1) {
      const entry = entries[entryIndex];
      const label = `${entryIndex + 1}. ${entry.title}`;
      applyFontWithFallback(doc, context.preferredFontId, label, "normal");
      ensureActiveFont(doc, "normal");
      doc.text(label, context.margin, cursorY, {
        maxWidth: pageWidth - context.margin * 2 - 40,
      });
      doc.text(String(entry.page), pageWidth - context.margin, cursorY, { align: "right" });
      cursorY += context.lineHeight;
      entryIndex += 1;
    }

    if (entryIndex >= entries.length && metadata?.settingsSummary) {
      cursorY += context.lineHeight * 0.6;
      doc.setFontSize(context.subheadingSize);
      doc.setTextColor(context.palette.muted);
      applyFontWithFallback(doc, context.preferredFontId, metadata.settingsSummary, "normal");
      ensureActiveFont(doc, "normal");
      doc.text(metadata.settingsSummary, context.margin, cursorY, {
        maxWidth: pageWidth - context.margin * 2,
      });
    }

    if (pageIdx === tocPages.length - 1 && metadata?.note) {
      doc.setFontSize(context.bodyFontSize - 1);
      doc.setTextColor(context.palette.muted);
      applyFontWithFallback(doc, context.preferredFontId, metadata.note, "normal");
      ensureActiveFont(doc, "normal");
      doc.text(metadata.note, context.margin, doc.internal.pageSize.getHeight() - context.margin, {
        maxWidth: pageWidth - context.margin * 2,
      });
    }
  });
};

const addPageNumbers = (doc, context, skipPages = [], metadata) => {
  const total = doc.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  for (let i = 1; i <= total; i += 1) {
    if (skipPages.includes(i)) continue;
    doc.setPage(i);
    // Header: left book/catalog, right category
    const headerLeft = metadata?.bookTitle || "Worship Songbook";
    const headerRight = (metadata?.categoryLabel || "").toUpperCase();
    const headerY = context.margin * 0.6;
    doc.setTextColor(context.palette.muted);
    doc.setFontSize(context.bodyFontSize - 2);
    applyFontWithFallback(doc, context.preferredFontId, headerLeft, "normal");
    ensureActiveFont(doc, "normal");
    doc.text(headerLeft, context.margin, headerY, { maxWidth: pageWidth / 2 - context.margin });
    if (headerRight) {
      doc.text(headerRight, pageWidth - context.margin, headerY, {
        align: "right",
        maxWidth: pageWidth / 2 - context.margin,
      });
    }

    applyFontWithFallback(doc, context.preferredFontId, `${i}`, "normal");
    ensureActiveFont(doc, "normal");
    doc.setFontSize(context.bodyFontSize - 2);
    doc.setTextColor(context.palette.muted);
    // Footer: page number left, song name right
    const y = pageHeight - context.margin / 2;
    doc.text(`Page ${i}/${total}`, context.margin, y);
    const songLabel = metadata?.currentSongTitles?.[i] || metadata?.bookTitle || "";
    if (songLabel) {
      doc.text(songLabel, pageWidth - context.margin, y, { align: "right" });
    }
  }
};

export const exportSongsToBookPdf = async (songs, options = {}) => {
  if (!Array.isArray(songs) || songs.length === 0) {
    throw new Error("No songs available for PDF export.");
  }

  const outputFilename = options.filename || `${DEFAULT_FILENAME}.pdf`;
  const context = prepareBookContext(options);
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "letter",
  });

  const loadLogoDataUrl = async () => {
    if (options.logoDataUrl) return options.logoDataUrl;
    const logoPath = `${process.env.PUBLIC_URL || ""}/logo192.png`;
    try {
      const res = await fetch(logoPath);
      const blob = await res.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      console.warn("Unable to load logo", e);
      return null;
    }
  };

  const runTextExport = async () => {
    try {
      await ensurePdfFonts(doc);
    } catch (fontError) {
      console.warn("Proceeding without custom fonts; falling back to built-in fonts.", fontError);
      availablePdfFonts.clear();
    }
    setFontSafe(doc, "helvetica", "normal");
    ensureActiveFont(doc, "normal");

    const pageHeight = doc.internal.pageSize.getHeight();
    const tocLinesPerPage = Math.max(
      1,
      Math.floor(
        (pageHeight - context.margin * 2 - context.lineHeight * 1.5) / context.lineHeight
      )
    );
    const tocPageCount = Math.max(1, Math.ceil(songs.length / tocLinesPerPage));

    const metadata = {
      bookTitle: options.bookTitle || "Worship Songbook",
      catalogLabel: (options.catalogLabel || "").toUpperCase(),
      categoryLabel: (options.categoryLabel || "").toUpperCase(),
      subtitle: options.subtitle,
      generatedAt: options.generatedAt || new Date().toLocaleString(),
      settingsSummary: context.settingsSummary,
      logoDataUrl: await loadLogoDataUrl(),
    };

    drawCoverPage(doc, context, metadata);

  const tocPages = [];
  for (let i = 0; i < tocPageCount; i += 1) {
    doc.addPage();
    tocPages.push(doc.getNumberOfPages());
  }

    const tocEntries = [];

    const pageSongMap = {};
    let layoutState = null;
    songs.forEach((song, index) => {
      if (!layoutState) {
        doc.addPage();
        fillPageBackground(doc, context.palette);
        layoutState = {
          page: doc.getNumberOfPages(),
          columnIndex: 0,
          cursorX: context.margin,
          cursorY: context.margin + context.lineHeight * 0.6,
        };
      }
      const startPage = layoutState.page;
      const { state: nextState, pagesUsed } = renderSongSection(doc, song, context, layoutState);
      layoutState = nextState;
      pagesUsed.forEach((p) => {
        pageSongMap[p] = song.title || `Song ${index + 1}`;
      });
      tocEntries.push({
        title: song.title || `Song ${index + 1}`,
        page: startPage,
      });
    });

    renderTableOfContents(doc, tocPages, tocEntries, context, metadata);
    addPageNumbers(doc, context, [1], {
      currentSongTitles: pageSongMap,
      bookTitle: metadata.bookTitle,
      categoryLabel: metadata.categoryLabel,
    });
    doc.save(outputFilename);
  };

  try {
    await runTextExport();
  } catch (error) {
    console.warn("Book text export failed; falling back to image-based PDF.", error);
    await exportSongsToPdfViaImages(songs, outputFilename, {
      background: "#ffffff",
      text: "#111111",
      headerBg: "#f5f5f5",
      headerBorder: "1px solid #dddddd",
      bodyPadding: "32px 48px",
    });
  }
};

export const exportSongsCollectionAsImages = async (songs, filename = `${DEFAULT_FILENAME}.zip`) => {
  if (!Array.isArray(songs) || songs.length === 0) {
    throw new Error("No songs available for image export.");
  }
  const zip = new JSZip();
  for (let i = 0; i < songs.length; i += 1) {
    const song = prepareSongForExport(songs[i]);
    const dataUrl = await renderSongToCanvas(song);
    const base64 = dataUrl.split(",")[1];
    const fileName = `${String(i + 1).padStart(2, "0")}-${(song.title || "song").replace(/[^a-z0-9\-_]+/gi, "_")}.png`;
    zip.file(fileName, base64, { base64: true });
  }
  const blob = await zip.generateAsync({ type: "blob" });
  triggerDownload(blob, filename);
};

export const buildSongPayload = (song) => {
  const formatted = formatLyricsForExport(song);
  return {
    title: song?.title || "Untitled Song",
    categories: ensureArray(song?.categories).filter(Boolean),
    lyrics: formatted.lyrics,
    linesPerSlide: formatted.linesPerSlide,
  };
};

export const exportSongsCollectionToText = (songs) => songs.map(buildSongPlaintext);

const sanitizeFileFragment = (value, fallback = "slide") => {
  const base = (value || fallback).toString().trim();
  return base
    .toLowerCase()
    .replace(/[^a-z0-9\-_.]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 64) || fallback;
};

export const exportImageCollectionFromDataUrls = async (items, filename = `${DEFAULT_FILENAME}.zip`) => {
  if (!Array.isArray(items) || items.length === 0) {
    throw new Error("No slides available for image export.");
  }
  const zip = new JSZip();
  items.forEach((item, index) => {
    if (!item?.dataUrl) return;
    const base64 = item.dataUrl.split(",")[1];
    if (!base64) return;
    const safeName = sanitizeFileFragment(item.title, `slide-${index + 1}`);
    const padded = String(index + 1).padStart(2, "0");
    zip.file(`${padded}-${safeName}.png`, base64, { base64: true });
  });
  const blob = await zip.generateAsync({ type: "blob" });
  triggerDownload(blob, filename);
};

export const exportPptFromDataUrls = async (items, filename = `${DEFAULT_FILENAME}.pptx`) => {
  if (!Array.isArray(items) || items.length === 0) {
    throw new Error("No slides available for PowerPoint export.");
  }
  const pptx = new PptxGenJS();
  pptx.layout = "LAYOUT_WIDE";
  const margin = 0.25;
  items.forEach((item) => {
    if (!item?.dataUrl) return;
    const slide = pptx.addSlide();
    const { width, height } = pptx.presLayout;
    slide.background = { color: "000000" };
    slide.addImage({
      data: item.dataUrl,
      x: margin,
      y: margin,
      w: width - margin * 2,
      h: height - margin * 2,
    });
  });
  await pptx.writeFile({ fileName: filename });
};
